import { motion } from "framer-motion";
import { X, Bookmark, Share2, BookmarkCheck, MessageCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { saveReflection, isReflectionSaved } from "@/lib/storage";

const DUST = Array.from({ length: 14 }).map((_, i) => ({
  x: 8 + (i * 47) % 92,
  y: 20 + (i * 37) % 60,
  delay: (i * 0.13) % 2,
  size: 2 + ((i * 3) % 4),
}));

export default function ReflectionCard({ reflection, onClose, onContinue }) {
  const [saved, setSaved] = useState(() => isReflectionSaved(reflection));
  const [burst, setBurst] = useState(false);

  const takeaway = reflection.takeaway || "Take one small step forward today with courage.";

  const handleSave = () => {
    saveReflection(reflection);
    setSaved(true);
    setBurst(true);
    setTimeout(() => setBurst(false), 900);
    toast.success("Reflection saved");
  };

  const handleShare = async () => {
    const shareText =
      `KALKI's Reflection\n\n` +
      `"${reflection.question}"\n\n` +
      `${takeaway}\n\n` +
      `— Shared from KALKI`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "KALKI's Reflection",
          text: shareText,
        });
        return;
      } catch (e) {
        // fallthrough
      }
    }
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Copied to clipboard");
    } catch (e) {
      toast.error("Unable to share");
    }
  };

  // trap focus lightly + escape close
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      data-testid="reflection-card"
    >
      <div className="absolute inset-0 bg-white/70 backdrop-blur-xl" onClick={onClose} />

      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ type: "spring", damping: 22, stiffness: 220 }}
        className="relative w-full sm:max-w-lg rounded-[28px] p-7 pb-6 overflow-hidden mb-6 sm:mb-0 kalki-shimmer-border"
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,251,241,0.98) 100%)",
          border: "1px solid rgba(197,160,89,0.35)",
          boxShadow:
            "0 30px 80px -20px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.6) inset",
        }}
      >
        {/* Peacock feather arc behind the card content */}
        <motion.svg
          aria-hidden
          viewBox="0 0 600 260"
          className="absolute -top-8 left-1/2 -translate-x-1/2 pointer-events-none"
          width="640"
          height="280"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 0.35, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
        >
          <defs>
            <linearGradient id="rc-arc" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#5BAD7E" stopOpacity="0" />
              <stop offset="25%" stopColor="#0FA3A3" stopOpacity="0.6" />
              <stop offset="55%" stopColor="#C5A059" stopOpacity="0.7" />
              <stop offset="80%" stopColor="#2E5FAB" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#5BAD7E" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M20 200 C 150 40, 450 40, 580 200"
            fill="none"
            stroke="url(#rc-arc)"
            strokeWidth="1.3"
          />
          <path
            d="M20 210 C 160 60, 440 60, 580 210"
            fill="none"
            stroke="url(#rc-arc)"
            strokeWidth="1"
            opacity="0.6"
          />
        </motion.svg>

        {/* Ambient halos */}
        <div
          aria-hidden
          className="kalki-halo"
          style={{ top: -120, left: -60, width: 300, height: 300 }}
        />
        <div
          aria-hidden
          className="kalki-halo"
          style={{ bottom: -140, right: -80, width: 340, height: 340, opacity: 0.6 }}
        />

        {/* Floating dust particles */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          {DUST.map((d, i) => (
            <span
              key={i}
              className="absolute rounded-full dust-float"
              style={{
                left: `${d.x}%`,
                top: `${d.y}%`,
                width: d.size,
                height: d.size,
                background: "rgba(197,160,89,0.55)",
                filter: "blur(0.5px)",
                animationDelay: `-${d.delay}s`,
              }}
            />
          ))}
        </div>

        <button
          data-testid="reflection-close"
          onClick={onClose}
          className="absolute top-4 right-4 h-8 w-8 rounded-full border border-neutral-100 flex items-center justify-center text-neutral-400 hover:text-neutral-900 hover:border-neutral-200 transition-colors z-10 bg-white/70 backdrop-blur"
          aria-label="Close"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.5 }}
            className="flex items-center gap-2"
          >
            <div className="gold-hairline w-6" />
            <span className="text-[10px] font-semibold tracking-[0.3em] uppercase gold-text">
              KALKI&rsquo;s Reflection
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.55 }}
            className="font-display mt-3 text-3xl sm:text-4xl font-light tracking-tight text-neutral-950"
          >
            One step from KALKI, for today.
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.55 }}
            className="mt-6 rounded-2xl border border-[#EFDDB2] bg-gradient-to-b from-[#FFFBF1] to-white p-5"
          >
            <p
              className="text-[15.5px] leading-relaxed text-neutral-900 font-medium"
              data-testid="reflection-action"
            >
              {takeaway}
            </p>
          </motion.div>

          {reflection.question && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35, duration: 0.5 }}
              className="mt-4 text-[12px] text-neutral-400 italic"
            >
              on &ldquo;{reflection.question.length > 100
                ? reflection.question.slice(0, 100) + "…"
                : reflection.question}&rdquo;
            </motion.p>
          )}

          <div className="mt-7 grid grid-cols-3 gap-2 relative">
            {/* Save burst on click */}
            {burst && (
              <motion.svg
                aria-hidden
                viewBox="0 0 80 80"
                className="absolute left-0 -top-3 w-20 h-20 pointer-events-none"
                initial={{ opacity: 0, scale: 0.4 }}
                animate={{ opacity: [0, 0.9, 0], scale: [0.4, 1.3, 1.5] }}
                transition={{ duration: 0.9 }}
              >
                <defs>
                  <radialGradient id="save-burst" r="0.5">
                    <stop offset="0%" stopColor="#F0D18A" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#C5A059" stopOpacity="0" />
                  </radialGradient>
                </defs>
                <circle cx="40" cy="40" r="36" fill="url(#save-burst)" />
              </motion.svg>
            )}

            <ActionButton
              testid="reflection-save"
              onClick={handleSave}
              icon={saved ? <BookmarkCheck className="w-3.5 h-3.5" /> : <Bookmark className="w-3.5 h-3.5" />}
              label={saved ? "Saved" : "Save Reflection"}
              highlighted={saved}
            />
            <ActionButton
              testid="reflection-share"
              onClick={handleShare}
              icon={<Share2 className="w-3.5 h-3.5" />}
              label="Share Wisdom"
              pulse
            />
            <ActionButton
              testid="reflection-continue"
              onClick={onContinue}
              icon={<MessageCircle className="w-3.5 h-3.5" />}
              label="Continue with KALKI"
            />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ActionButton({ onClick, icon, label, testid, highlighted, pulse }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={
        "relative flex flex-col items-center justify-center gap-1.5 rounded-2xl py-3.5 px-2 text-[11.5px] font-medium transition-all active:scale-[0.97] text-center overflow-hidden liquid-gold " +
        (highlighted
          ? "bg-gradient-to-b from-[#FFF7E4] to-white border border-[#EFDDB2] text-[#8C6B29]"
          : "bg-white border border-neutral-100 text-neutral-700 hover:border-neutral-200 hover:bg-neutral-50")
      }
    >
      {pulse && <span aria-hidden className="share-pulse" />}
      <span className="relative">{icon}</span>
      <span className="relative leading-tight">{label}</span>
    </button>
  );
}
